import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PageConfig from '@/component/layout/PageConfig.tsx'
import DashboardMap from '@/pages/dashboard/DashboardMapDefault.tsx'
import UrgentNotification from '@/pages/dashboard/UrgentNotification.tsx'
import { isNotEmptyArray } from '@/util/array.ts'
import useQueryGetUsersBbox, { IReqGetUsersBbox } from '@/api/user/getUsersBbox.ts'
import { AlertStatus, DeviceStatus } from '@/api/user/getUsers.ts'
import useQueryGetMe, { DashboardType } from '@/api/auth/getMe.ts'
import useQueryGetAlertBioLive from '@/api/notification/getAlertBioLive.ts'
import BioAlertDetailModal from '@/pages/notification/bio/BioAlertModal.tsx'
import useQueryGetMyPermission, { PermissionAction, PermissionSubject } from '@/api/auth/getPermission.ts'
import AlertStatusBadge from '@/component/badge/AlertStatusBadge.tsx'
import useQueryGetLiveEmergencyAlert from '@/api/notification/getLiveEmergencyAlert.ts'
import useQueryGetLiveFallDetectionAlert from '@/api/notification/getLiveFallDetectionAlert.ts'
import DeviceStatusBadge from '@/component/badge/DeviceStatusBadge.tsx'
import DashboardMapForCareManager from '@/pages/dashboard/DashboardMapForCareManager.tsx'
import { useTranslation } from 'react-i18next'
import useQueryGetUsersDeviceStatus from '@/api/user/getUsersDeviceStatus'
import { useMapLocationStore } from '@/store/MapLocationStore.ts'


type IProps = {}

const Dashboard: React.FC<IProps> = ({}) => {
    const { t } = useTranslation()
    const { hasPermission } = useQueryGetMyPermission()
    const [bbox, setBbox] = useState<IReqGetUsersBbox>()
    const {
        data: bioAlertRes,
        refetch: refetchBioAlerts
    } = useQueryGetAlertBioLive(hasPermission(PermissionSubject.ALERT_BIO, PermissionAction.READ))
    const { data: users } = useQueryGetUsersBbox(bbox)
    const { data: me } = useQueryGetMe()
    const { data: deviceStatus } = useQueryGetUsersDeviceStatus()
    const { data: emergencyAlerts } = useQueryGetLiveEmergencyAlert()
    const { data: fallDetectionAlerts } = useQueryGetLiveFallDetectionAlert()
    const mapRef = useRef<any>()
    const { consumeTargetLocation } = useMapLocationStore()

    const totalUserDeviceCount = useMemo(() => {
        if (!deviceStatus) return 0
        return Object.values(deviceStatus).reduce((sum, currentValue) => sum + currentValue, 0);
    }, [deviceStatus])

    const markers = useMemo(() => {
        return users?.filter((user) => user?.location)
    }, [users])

    const [detailModalId, setDetailModalId] = useState<number>(-1)
    const detailModalRef = useRef<any>()

    // const markers = useMemo(() => {
    //     return users?.filter(item => {
    //         // 실시간 상태 데이터가 있는 경우 우선 사용
    //         // TODO: 배터리 통계 계산 로직 추가
    //         // const recentState = item.recentState?.summary
    //         // if (recentState) {
    //         //     if (recentState.isCharging) return true
    //         //     if (recentState.isOnline !== undefined) return true
    //         // }
    //         return item?.deviceStatus
    //     })
    // }, [users])

    const isCareManager = useMemo(
        () => me?.dashboardType == DashboardType.CARE_MANAGER,
        [me?.dashboardType]
    )

    // 실시간 상태를 고려한 디바이스 상태 계산
    // const getActualDeviceStatus = (user: IUserBbox) => {
        
        
    //     // TODO: 배터리 통계 계산 로직 추가
    //     // const recentState = user.recentState?.summary
        
    //     // 실시간 충전 상태 우선 확인
    //     // if (recentState?.isCharging) return DeviceStatus.CHARGING
        
    //     // 실시간 온라인 상태 확인
    //     // if (recentState?.isOnline !== undefined) {
    //         // return recentState.isOnline ? DeviceStatus.ONLINE : DeviceStatus.OFFLINE
    //     // }
        
    //     // 기본 device.status 사용
    //     return user.deviceStatus
    // }

    // 배터리 통계 계산
    // TODO: 배터리 통계 계산 로직 추가
    // const batteryStats = useMemo(() => {
    //     if (!users) return { lowBatteryUsers: [] }
        
    //     const lowBatteryUsers = users.filter(user => {
    //         const battery = user.recentState?.summary?.battery ?? user.device?.battery
    //         const status = getActualDeviceStatus(user)
    //         return battery !== undefined && 
    //                battery < 15 && 
    //                status !== DeviceStatus.CHARGING
    //     })

    //     return { lowBatteryUsers }
    // }, [users])

    const handleBoundsChange = useCallback((newBbox: IReqGetUsersBbox) => {
        setBbox(currentBbox => {
            // 새로 계산된 영역이 현재 영역과 다를 때만 상태를 업데이트하여 무한 루프를 방지합니다.
            if (JSON.stringify(currentBbox) === JSON.stringify(newBbox)) {
                return currentBbox;
            }
            return newBbox;
        });
    }, []);

    // 알림에서 위치 버튼을 클릭했을 때 지도 이동
    useEffect(() => {
        const targetLocation = consumeTargetLocation()
        if (targetLocation && mapRef.current) {
            const { latitude, longitude } = targetLocation
            // 위도/경도 값이 유효한지 확인
            if (
                typeof latitude === 'number' && 
                typeof longitude === 'number' && 
                !isNaN(latitude) && 
                !isNaN(longitude) &&
                isFinite(latitude) && 
                isFinite(longitude)
            ) {
                mapRef.current.setLocation(latitude, longitude, 16)
            } else {
                console.warn('Invalid target location coordinates:', targetLocation)
            }
        }
    }, [consumeTargetLocation])
    return <>
        <PageConfig showTopNav={true} showSideNav={true} className={'min-w-[800px]'}>
            <div className={'relative h-full overflow-hidden'}>
                {!isCareManager ? <DashboardMap users={markers} className={'h-full w-full'} ref={mapRef} onBoundsChange={handleBoundsChange} /> :
                    <DashboardMapForCareManager users={markers} className={'h-full w-full'} ref={mapRef} onBoundsChange={handleBoundsChange} />}

                <div
                    className={'w-full flex h-full justify-between overflow-hidden absolute top-12 pointer-events-none'}>
                    <div className={'flex gap-[15px] justify-between items-start pl-[15px] pt-[15px]'}
                         style={{ width: '100%' }}>

                        {isCareManager ? <div
                            className={'bg-white px-[18px] py-[21px] border border-solid border-gray2 flex flex-col w-[280px] font-medium gap-[8px]'}>
                            <p className={'text-center px-[10px] py-[8px] text-lg font-bold'}>{t('V3.dashboard.totalUsers', { count: totalUserDeviceCount })}</p>
                            <div className={'w-full h-[1px] bg-gray2 my-[8px]'} />
                            
                            {/* 상태별 카운트 */}
                            <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                <DeviceStatusBadge value={DeviceStatus.EMERGENCY} />
                                <p>{t('V3.dashboard.status.emergency', { count: deviceStatus?.emergency || 0 })}</p>
                            </div>
                            <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                <DeviceStatusBadge value={DeviceStatus.ONLINE} />
                                <p>{t('V3.dashboard.status.online', { count: deviceStatus?.online || 0 })}</p>
                            </div>
                            <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                <DeviceStatusBadge value={DeviceStatus.CHARGING} />
                                <p>{t('V3.dashboard.status.charging', { count: deviceStatus?.charging || 0 })}</p>
                            </div>
                            <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                <DeviceStatusBadge value={DeviceStatus.OFFLINE} />
                                <p>{t('V3.dashboard.status.offline', { count: deviceStatus?.offline || 0 })}</p>
                            </div>



                            {me && me?.latitude && me?.longitude &&
                                <button className={'pointer-events-auto py-[8.5px] text-white bg-main_b mt-2'}
                                        onClick={() => {
                                            const latitude = me.latitude
                                            const longitude = me.longitude
                                            
                                            if (
                                                typeof latitude === 'number' && 
                                                typeof longitude === 'number' && 
                                                !isNaN(latitude) && 
                                                !isNaN(longitude) &&
                                                isFinite(latitude) && 
                                                isFinite(longitude)
                                            ) {
                                                mapRef?.current?.setLocation(latitude, longitude, 18)
                                            } else {
                                                console.warn('Center location not available or invalid:', { latitude, longitude })
                                            }
                                        }}>{t('dashboard.summary.findCenterLocation')}</button>}
                        </div> : <div
                            className={'bg-white px-[18px] py-[21px] border border-solid border-gray2 flex flex-col font-medium gap-[8px]'}>
                            <p className={'text-center px-[10px] py-[8px]'}>{t('dashboard.summary.title', { count: totalUserDeviceCount })}</p>
                            <div className={'w-full h-[1px] bg-gray2 my-[8px]'} />
                            <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                <AlertStatusBadge value={AlertStatus.EMERGENCY} />
                                <p>{t('dashboard.summary.emergency', { count: (emergencyAlerts?.emergencies?.length || 0) + (fallDetectionAlerts?.falls?.length || 0) })}</p>
                            </div>
                            <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                <AlertStatusBadge value={AlertStatus.BIO} />
                                <p>{t('dashboard.summary.bio', { count: bioAlertRes?.bios?.length || 0 })}</p>
                            </div>
                            


                            {me && me?.latitude && me?.longitude &&
                                <button className={'pointer-events-auto py-[8.5px] text-white bg-main_b'}
                                        onClick={() => {
                                            const latitude = me.latitude
                                            const longitude = me.longitude
                                            
                                            if (
                                                typeof latitude === 'number' && 
                                                typeof longitude === 'number' && 
                                                !isNaN(latitude) && 
                                                !isNaN(longitude) &&
                                                isFinite(latitude) && 
                                                isFinite(longitude)
                                            ) {
                                                mapRef?.current?.setLocation(latitude, longitude, 18)
                                            } else {
                                                console.warn('Center location not available or invalid:', { latitude, longitude })
                                            }
                                        }}>{t('dashboard.summary.findCenterLocation')}</button>}
                        </div>}

                    </div>
                    <div
                        className={'flex flex-col overflow-y-auto items-end'}
                        style={{ position: 'relative', left: '-40px', width: '100%', height: '92%' }} >
                        <div className={'flex flex-col pointer-events-auto pr-[15px] pt-[15px]'}>
                            {/* 배터리 부족 알림을 최상단에 표시 */}
                            {/* TODO: 배터리 통계 계산 로직 추가
                            {batteryStats.lowBatteryUsers.length > 0 && (
                                <div className={'bg-red-100 border border-red-300 rounded-lg p-4 mb-[10px] shadow-lg max-w-[300px]'}>
                                    <div className={'flex items-center gap-2 mb-2'}>
                                        <span className={'text-red-600 text-lg'}>🔋</span>
                                        <h3 className={'font-bold text-red-800'}>배터리 부족 알림</h3>
                                    </div>
                                    <div className={'space-y-2'}>
                                        {batteryStats.lowBatteryUsers.slice(0, 3).map(user => (
                                            <div key={user.id} className={'flex items-center justify-between bg-white rounded p-2'}>
                                                <span className={'text-sm font-medium text-gray-900'}>{user.name}</span>
                                                <BatteryStatusBadge
                                                    battery={user.device?.battery || 0}
                                                    status={user.device?.status}
                                                    showPercentage={true}
                                                />
                                            </div>
                                        ))}
                                        {batteryStats.lowBatteryUsers.length > 3 && (
                                            <p className={'text-xs text-red-600 text-center'}>
                                                외 {batteryStats.lowBatteryUsers.length - 3}명 더...
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )} */}

                            {hasPermission(PermissionSubject.ALERT_BIO, PermissionAction.READ) && bioAlertRes && isNotEmptyArray(bioAlertRes.bios) && bioAlertRes.bios?.map(item =>
                                <UrgentNotification key={item?.id}
                                          className={'shadow-lg mb-[10px]'}  {...item}
                                          onRefreshList={refetchBioAlerts}
                                          onClickMarker={(latitude, longitude) => {
                                              if (
                                                  typeof latitude === 'number' &&
                                                  typeof longitude === 'number' &&
                                                  !isNaN(latitude) &&
                                                  !isNaN(longitude) &&
                                                  isFinite(latitude) &&
                                                  isFinite(longitude)
                                              ) {
                                                  mapRef.current?.setLocation(latitude, longitude, 18)
                                              } else {
                                                  console.warn('Alert location not available or invalid:', { latitude, longitude })
                                              }
                                          }}
                                          onClickItem={id => {
                                              setDetailModalId(id)
                                              detailModalRef.current?.show()
                                          }}
                                />)}
                        </div>
                    </div>
                </div>
            </div>
        </PageConfig>
        <BioAlertDetailModal ref={detailModalRef} defaultId={detailModalId} refetchAlerts={refetchBioAlerts} />
    </>
}
export default Dashboard